import {ChangeDetectorRef, Component, Injector, Input, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {BaseResultListComponent} from '@twpub/shared/components/base';
import {
  Column,
  ColumnConfiguration,
  ConceptSelection,
  DisplayColumn,
  DisplayConcept,
  DisplayField,
  DomainTree,
  FieldProps,
  Language,
  PaginationData,
  ResultList,
  SessionObject,
  TermSearchResult
} from '@twpub/core/models';
import {ResultListProp} from '@twpub/core/constants';
import {
  ClientConfigurationScheme,
  ComponentConfiguration,
  ComponentInit,
  fromPageEvent,
  InitialConfig,
  updateConceptSelection
} from '@twpub/core/utils';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {MatDialog} from '@angular/material/dialog';
import _ from 'lodash';
import {
  ColumnConfigEditorComponent
} from '@twpub/shared/components/column-config-editor/column-config-editor.component';
import {DomainService, FeatureFlagService} from '@twpub/core/services';
import {BrowserStorageUtils, formatDomainPaths} from '@twpub/shared/utils';
import {catchError, forkJoin, map, of} from 'rxjs';
import {NgClass} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import {FieldRenderComponent, TermRenderComponent} from '@twpub/shared/components/renders';
import {MatIconButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ResultViewWrapperComponent} from '@twpub/shared/components/wrappers';
import {TippyTooltipDirective} from '@twpub/shared/directives/tippy-tooltip.directive';
import {GetDisplayFieldPipe} from './get-display-field.pipe';

const COLUMN_CONFIG = 'columnConfig';
const SHOW_TOOLTIPS = 'disableTooltips';
const ALLOW_USER_COLUMN_CONFIG = 'allowUserColumnConfig';

const {setLocalStorage, removeLocalStorage, COMBO_RESULT_LIST_COLUMN_CONFIG_KEY} = BrowserStorageUtils;

@Component({
  selector: 'pub-combo-result-list',
  templateUrl: './combo-result-list.component.html',
  styleUrls: ['./combo-result-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  standalone: true,
  imports: [NgClass, MatIcon, MatTooltip, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, TermRenderComponent, FieldRenderComponent, MatIconButton, MatProgressSpinner, ResultViewWrapperComponent, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, TippyTooltipDirective, MatPaginator, GetDisplayFieldPipe]
})
export class ComboResultListComponent extends BaseResultListComponent implements ResultList, OnInit {
  @Input() selectedLang?: string;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  domainService: DomainService;
  expandedRes?: TermSearchResult;
  concept?: DisplayConcept
  sourceColumnName: string = '';
  columnsToDisplay: DisplayColumn[] = [];
  columnIDsToDisplay: string[] = [];
  showTooltips: boolean = false;
  showConfigButton: boolean = false;
  private featureFlagsReady: boolean = false;
  langCodesToRender: string[] = [];
  detailsColSpan: number = 0;
  dictionaryIds: number[] = [];
  isLoadingResults: boolean = false;

  // Localized labels
  loadingResultsLabel = $localize`:@@comboResultList.loadingResults:Loading results...`;
  noResultsLabel = $localize`:@@comboResultList.noResults:Could not find any results`;
  noResultsForLabel = $localize`:@@comboResultList.noResultsFor:for`;
  sourceLabel = $localize`:@@comboResultList.source:Source`;
  dictionaryLabel = $localize`:@@comboResultList.dictionary:Dictionary`;
  sectionLabel = $localize`:@@comboResultList.section:Section`;
  domainLabel = $localize`:@@comboResultList.domain:Domain`;

  columnConfig?: ColumnConfiguration;
  private rowTooltipCache = new Map<string, string>();

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [
      {
        key: ResultListProp.InitialSearch,
        label: 'Results visible on page load',
        controlType: ControlType.Boolean,
        description: 'Automatically list all terms when page is opened, before any search'
      },
      {
        key: SHOW_TOOLTIPS,
        label: 'Show tooltips on rows',
        controlType: ControlType.Boolean,
        description: 'Show tooltips on rows with dictionary and section information'
      },
      {
        key: ALLOW_USER_COLUMN_CONFIG,
        label: 'Allow user column configuration',
        controlType: ControlType.Boolean,
        description: 'Allows the user to configure which columns to display'
      },
      {
        key: COLUMN_CONFIG,
        label: 'Column configuration',
        controlType: ControlType.JSON // todo Should have Editor type but existing data uses JSON
      }
    ];

    initialConfig = new Map([[ResultListProp.NeedSearchForEntry, true]]);

    constructor() {
      super(ComboResultListComponent, 'ComboResultListComponent', 'Combo Result List', [ComponentCategory.ResultList]);
    }

    createConfig(initialConfig: InitialConfig = this.initialConfig) {
      return new ComponentConfiguration<ResultList>(ComboResultListComponent, this.name, initialConfig);
    }
  }

  constructor(injector: Injector, private dialog: MatDialog, private cdr: ChangeDetectorRef, private featureFlagService: FeatureFlagService) {
    super(injector);
    this.domainService = injector.get(DomainService);
  }

  ngOnInit(): void {
    const pData = this.sharedStateService.getPaginationData();
    pData.pageSize = 15;
    this.sharedStateService.setPaginationData(pData);
    this.showTooltips = this.config.getBoolean(SHOW_TOOLTIPS);
    this.dictService.getDictionaries(true).pipe(
      map((dictionaries: any[]) => (dictionaries || []).map(d => d.id))
    ).subscribe((dictionaries: any[]) => this.dictionaryIds = dictionaries);

    // Wait for feature flags to be ready using polling
    // This is more reliable than subscriptions which can have timing issues
    this.featureFlagService.waitForReady(() => {
      this.featureFlagsReady = true;
      this.updateShowConfigButton();
      this.cdr.detectChanges();
    });

    this.onInit();
  }

  override doUpdate() {
    super.doUpdate();
    // Only update config button if feature flags are ready
    if (this.featureFlagsReady) {
      this.updateShowConfigButton();
    }
  }

  private updateShowConfigButton() {
    const columnConfigEnabled = this.isColumnConfigFeatureEnabled();
    this.showConfigButton = columnConfigEnabled && this.config.getBoolean(ALLOW_USER_COLUMN_CONFIG);
    if (!this.showConfigButton) {
      removeLocalStorage(COMBO_RESULT_LIST_COLUMN_CONFIG_KEY);
    }
  }

  isColumnConfigFeatureEnabled = () => {
    return true; // Feature is always enabled
  }

  override fetchLangNames() {
    // Set loading state instead of clearing results to prevent UI blinking
    this.isLoadingResults = true;

    // We need to set up columns BEFORE the parent calls performSearch
    // So we fetch languages ourselves first, then set up columns, then call parent's logic
    this.dictIds = this.sharedStateService.getDictionaryIds();
    this.sourceLangCodes = this.sharedStateService.getSourceLangs();
    this.targetLangCodes = this.sharedStateService.getTargetLangs();
    this.dictService.getLanguages(this.dictIds).subscribe({
      next: (langs) => {
        this.languages = langs;
        this.setSourceLangName(langs);

        // Set up columns before performSearch is called
        this.columnsToDisplay = this.findColumnsToDisplay();
        const colIds = this.columnsToDisplay.map(col => col.columnId || '').filter(Boolean);
        colIds.push('expand');
        const columnsChanged = !_.isEqual(colIds, this.columnIDsToDisplay);
        if (columnsChanged) {
          this.columnIDsToDisplay = colIds;
        }

        // Now perform the search with columns already configured
        this.performSearch(this.sharedStateService.getPaginationData(), true);
      },
      error: () => {
        this.isLoadingResults = false;
      }
    });
  }

  private findColumnsToDisplay = (): DisplayColumn[] => {
    let cols = (this.getColumnConfig().columns || []).filter(col => !col.lang || this.isLanguage(col.lang)) as DisplayColumn[];
    cols.forEach(col => {
      if (col.lang?.length) {
        col.isTermName = this.isTermNameColumn(col);
        col.columnId = 'translation-' + col.lang + (col.isTermName ? '' : '-' + col.field);
      } else {
        col.columnId = 'cf-' + col.field;
      }
    });
    const srcLang = this.sourceLangCodes[0];
    // Always show source term as the first column; remove any duplicate from config
    cols = cols.filter(col => !(col.lang === srcLang && this.isTermNameColumn(col)));
    cols = [{
      field: 'term',
      lang: srcLang,
      isTermName: true,
      columnId: 'termName'
    }, ...cols];
    let targetLangCodes = this.sharedStateService.getTargetLangs();
    if (!targetLangCodes?.length) targetLangCodes = this.languages?.map(lang => lang.code);
    targetLangCodes = targetLangCodes.filter(code => !this.sourceLangCodes.includes(code));

    const config = this.getColumnConfig();
    if (config.limitCols && config.limitCols > 0) {
      targetLangCodes = targetLangCodes.slice(0, config.limitCols);
    }

    this.langCodesToRender = targetLangCodes;
    this.detailsColSpan = targetLangCodes.length + 1; // +1 for source

    targetLangCodes = targetLangCodes.filter(code => !cols.some(col => col.lang === code && this.isTermNameColumn(col)));

    targetLangCodes.forEach(code => {
      const col = {
        field: 'term',
        lang: code,
        isTermName: true,
        columnId: 'termName-tgt-' + code
      };
      cols.push(col);
    });
    return cols.map(col => ({
      ...col,
      header: this.getHeader(col)
    }));
  }

  private getHeader = (col: DisplayColumn) => {
    if (col.lang?.length && col.isTermName) {
      return this.languageNameOf(col.lang);
    } else {
      return _.capitalize(col.label || col.field || '');
    }
  }

  private languageNameOf(langCode?: string): string {
    return this.languages.find(l => l.code === (langCode))?.name || '';
  }

  private setSourceLangName = (langs: Language[]) => {
    if (this.sourceLangCodes.length === 1) {
      const code = this.sourceLangCodes[0];
      this.sourceColumnName = this.dictService.getLanguage(langs, code)?.name || code;
    } else {
      this.sourceColumnName = this.sourceLabel;
    }
  }

  private getColumnConfig = (): ColumnConfiguration => {
    if (!this.columnConfig) {
      const storedConfig = BrowserStorageUtils.getLocalStorage(BrowserStorageUtils.COMBO_RESULT_LIST_COLUMN_CONFIG_KEY);
      if (storedConfig) {
        this.columnConfig = JSON.parse(storedConfig) as ColumnConfiguration;
      } else {
        const columnConfigJson = this.config?.getString(COLUMN_CONFIG) || '{}';
        this.columnConfig = JSON.parse(columnConfigJson) as ColumnConfiguration;
      }
    }
    return this.columnConfig;
  }

  pageChange(event: PageEvent) {
    this.performSearch(fromPageEvent(event), true);
  }

  override performSearch(pData?: PaginationData, clear: boolean = false, callbackFn?: (resultIndex?: number) => void) {
    this.searchConfig.columns = this.columnsToDisplay;
    this.isLoadingResults = true;

    super.performSearch(pData, clear, (idx) => {
      callbackFn?.(idx);

      // The parent already set this.dataSource.data = this.results, but we need to clear it
      // to prevent rendering with incomplete data (missing DICT/SECT/DOM fields)
      this.dataSource.data = [];

      // Load async data and update dataSource when complete
      this.loadAsyncDataAndBuildCache(this.results || []);
    });

    // If super.performSearch returned without initiating a search (early return for
    // duplicate criteria, or no search needed), reset the loading state
    if (!this.isLoading) {
      this.isLoadingResults = false;
    }
  }

  override resultClick(res: TermSearchResult, index?: number): void {
    this.expandedRes = this.expandedRes === res ? undefined : res
    if (this.expandedRes) {
      super.resultClick(res, index, true);
    } else {
      this.sharedStateService.clearConcept();
    }
  }

  /**
   * Concept selection by following an internal link.
   * @param selection The concept selection object
   */
  override selectConcept(selection: ConceptSelection) {
    this.openConceptDetails(selection);
  }

  async openConceptDetails(selection: ConceptSelection): Promise<void> {
    const sessionObj = new SessionObject(this.sharedStateService.sessionObj);
    updateConceptSelection(sessionObj, selection)
    const data = {sessionObj, configId: this.sharedStateService.getConfigId()};
    const {ConceptViewDialogComponent} = await import('@twpub/shared/components/concept-view-dialog/concept-view-dialog.component');
    this.dialog.open(ConceptViewDialogComponent, {width: '80%', height: '80%', data});
  }

  openColumnConfigDialog(): void {
    const oldConfig = this.getColumnConfig();
    const dialogRef = this.dialog.open(ColumnConfigEditorComponent, {
      width: '80%',
      data: {
        dictionaryIds: this.dictionaryIds,
        columnConfig: oldConfig
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const needsRefetch = this.hasColumnsAddedOrModified(oldConfig, result);
        this.columnConfig = result;
        setLocalStorage(COMBO_RESULT_LIST_COLUMN_CONFIG_KEY, JSON.stringify(result));
        // Refresh the columns
        this.columnsToDisplay = this.findColumnsToDisplay();
        this.columnIDsToDisplay = this.columnsToDisplay.map(col => col.columnId || '').filter(Boolean);
        this.columnIDsToDisplay.push('expand');

        // Re-fetch results if columns were added or modified (not just removed)
        if (needsRefetch) {
          this.performSearch(this.sharedStateService.getPaginationData(), true);
        }
      }
    });
  }

  /**
   * Checks if columns were added or modified (field changed).
   * Returns true if re-fetch is needed.
   */
  private hasColumnsAddedOrModified(oldConfig: ColumnConfiguration, newConfig: ColumnConfiguration): boolean {
    const oldColumns = oldConfig.columns || [];
    const newColumns = newConfig.columns || [];

    // If new config has more columns, a column was added
    if (newColumns.length > oldColumns.length) {
      return true;
    }

    // Check if any column's field or lang was modified
    for (let i = 0; i < newColumns.length; i++) {
      const newCol = newColumns[i];
      const oldCol = oldColumns[i];

      // If there's no corresponding old column at this position, or if field/lang changed
      if (!oldCol || newCol.field !== oldCol.field || newCol.lang !== oldCol.lang) {
        return true;
      }
    }

    return false;
  }

  protected override shouldOpenSingleResult = (): boolean => true;

  trackByColumnId(index: number, col: DisplayColumn): string {
    return col.columnId!;
  }

  private loadAsyncDataAndBuildCache(results: TermSearchResult[]): void {
    if (!results?.length) {
      // No results to enrich, but the loading overlay must still be dismissed
      // so it does not overlap the "no results" message.
      this.isLoadingResults = false;
      this.cdr.detectChanges();
      return;
    }

    // Build displayFieldsMap using composite keys (jsonField:level) from server's level field
    for (const res of results) {
      const resAny = res as any;
      resAny.displayFieldsMap = new Map<string, DisplayField>();

      res.displayFields?.forEach(df => {
        // Use composite key: jsonField:level:lang (e.g., "Definition:concept" or "Grammar:term:sv")
        let key = df.jsonField + ':' + (df.level || 'concept');
        if (df.lang) {
          key += ':' + df.lang;
        }
        resAny.displayFieldsMap.set(key, df);
      });
    }

    // Then load async data and add special fields when data arrives
    const hasDictCol = this.columnsToDisplay.some(col => col.field === 'DICT');
    const hasSectCol = this.columnsToDisplay.some(col => col.field === 'SECT');
    const hasDomCol = this.columnsToDisplay.some(col => col.field === 'DOM');

    // Collect unique dictionary IDs to avoid duplicate domain tree fetches
    const uniqueDictIds = [...new Set(results.map(res => res.term.dictionaryId))];

    // Fetch domain trees for all unique dictionary IDs in parallel first
    const domainTreeRequests = uniqueDictIds.map(dictId =>
      this.domainService.getDomainTree([dictId]).pipe(
        map(tree => ({dictId, tree})),
        catchError(err => {
          this.logger.warn('Failed to load domain tree for dict', dictId, err);
          return of({dictId, tree: new DomainTree()});
        })
      )
    );

    // First fetch all domain trees, then process results
    const domainTree$ = domainTreeRequests.length
      ? forkJoin(domainTreeRequests)
      : of([] as { dictId: number, tree: DomainTree }[]);

    domainTree$.subscribe({
      next: (domainTreeResults) => {
      // Build a map of dictId -> domainTree for quick lookup
      const domainTreeMap = new Map<number, DomainTree>();
      domainTreeResults.forEach(({dictId, tree}) => {
        domainTreeMap.set(dictId, tree);
      });

      // Now fetch dictionary data for each result (these are likely cached by dictService)
      const dictRequests = results.map(res =>
        this.dictService.getDictionary(res.term.dictionaryId).pipe(
          map(dict => ({res, dict})),
          catchError(err => {
            this.logger.warn('Failed to load dictionary', res.term.dictionaryId, err);
            return of({res, dict: null as any});
          })
        )
      );

      forkJoin(dictRequests).subscribe((dictResponses) => {
        dictResponses.forEach(({res, dict}) => {
          const resAny = res as any;
          const domainTree = domainTreeMap.get(res.term.dictionaryId);

          const dictName = dict?.name || '';
          const section = dict?.sections?.find((s: any) => s.oid === res.term.sectionOid)?.name || '';
          resAny.dictionaryName = dictName;
          resAny.sectionName = section;

          // Create a new Map instance based on the existing one so pure pipes see the change
          const newDisplayFieldsMap = new Map<string, DisplayField>(resAny.displayFieldsMap);

          // Add DICT field with actual value (concept-level)
          if (hasDictCol) {
            newDisplayFieldsMap.set('DICT:concept', {
              oid: '',
              label: '',
              value: dictName,
              jsonField: 'DICT'
            });
          }

          // Add SECT field with actual value (concept-level)
          if (hasSectCol) {
            newDisplayFieldsMap.set('SECT:concept', {
              oid: '',
              label: '',
              value: section,
              jsonField: 'SECT'
            });
          }

          const domainIds = res.term.domainIds || [];
          const lines: string[] = [`${this.dictionaryLabel}: ${dictName}`];
          if (section) lines.push(`${this.sectionLabel}: ${section}`);

          let domainText = '';
          if (domainIds?.length && domainTree) {
            const paths = domainIds.map((id: number) => domainTree.getPath(id));
            domainText = formatDomainPaths(paths);
            lines.push(`${this.domainLabel}: ${domainText}`);
            resAny.domainName = domainText;
          }

          if (hasDomCol) {
            newDisplayFieldsMap.set('DOM:concept', {
              oid: '',
              label: '',
              value: domainText,
              jsonField: 'DOM'
            });
          }

          // Reassign the new map so pure pipe sees a changed reference
          resAny.displayFieldsMap = newDisplayFieldsMap;

          this.rowTooltipCache.set(String(res.term.id), lines.join('\n'));
        });

        // Now update the dataSource so the view renders with complete data
        this.dataSource.data = this.results || [];

        // Clear loading state
        this.isLoadingResults = false;

        // Single detectChanges after all async updates
        this.cdr.detectChanges();
      });
      },
      error: (err) => {
        this.logger.error('Failed to load async data for results, showing results without enrichment:', err);
        this.dataSource.data = this.results || [];
        this.isLoadingResults = false;
        this.cdr.detectChanges();
      }
    });
  }

  getRowTooltip = (res: TermSearchResult): string => this.rowTooltipCache.get(String(res.term.id)) || '';

  isTermNameColumn = (col: Column) => !col.field?.length || col.field === 'TERM';

  getTerm(res: TermSearchResult, col: Column) {
    const langCode = col.lang!;
    if (this.sourceLangCodes.includes(langCode)) {
      return res.term;
    }
    return res.translations?.find(t => t.languageCode === langCode && langCode !== res.term.languageCode);
  }
}
